'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

function SendIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

export default function ContactForm() {
  const t = useTranslations('mainPage.contact.form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      message: formData.get('message'),
    };

    // Basic validation
    if (!data.name || !data.phone) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      setIsSubmitting(false);
      return;
    }

    // Phone validation
    const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
    if (!phoneRegex.test(data.phone as string)) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number.' });
      setIsSubmitting(false);
      return;
    }

    // Email validation (if provided)
    if (data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email as string)) {
        setMessage({ type: 'error', text: 'Please enter a valid email address.' });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        (e.target as HTMLFormElement).reset();
      } else {
        setMessage({ type: 'error', text: result.message || 'Error sending message. Please call us.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Sorry, there was an error. Please call us at +592 763-8844.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form">
      <h3>{t('title')}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">{t('fullName')}</label>
            <input type="text" id="name" name="name" required placeholder={t('namePlaceholder')} />
          </div>
          <div className="form-group">
            <label htmlFor="phone">{t('phone')}</label>
            <input type="tel" id="phone" name="phone" required placeholder={t('phonePlaceholder')} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="email">{t('email')}</label>
          <input type="email" id="email" name="email" placeholder={t('emailPlaceholder')} />
        </div>
        <div className="form-group">
          <label htmlFor="message">{t('message')}</label>
          <textarea id="message" name="message" placeholder={t('messagePlaceholder')}></textarea>
        </div>
        <div className="form-group form-consent">
          <label className="consent-label">
            <input type="checkbox" id="privacy-consent" name="privacy-consent" required />
            <span>{t('consent')} <a href="/privacy" target="_blank">{t('privacy')}</a> {t('and')} <a href="/terms" target="_blank">{t('terms')}</a></span>
          </label>
        </div>
        {message && (
          <div
            className={`form-message form-message-${message.type}`}
            style={{
              padding: '1rem',
              marginBottom: '1rem',
              borderRadius: '0.5rem',
              fontWeight: 500,
              backgroundColor: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
              color: message.type === 'success' ? '#065F46' : '#991B1B',
              border: `1px solid ${message.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
            }}
          >
            {message.text}
          </div>
        )}
        <div className="form-submit">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : t('submit')}
            {!isSubmitting && <SendIcon />}
          </button>
        </div>
      </form>
    </div>
  );
}
